import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import Spin from '@hellodarwin/core/lib/components/common/spin';
import Typography from '@hellodarwin/core/lib/components/common/typography';
import HdButton from '@hellodarwin/core/lib/components/common/button';
import { useTranslations } from '@hellodarwin/core/lib/features/providers/translations-provider';
import Chevron from '@hellodarwin/icons/dist/icons/Chevron';
import FileIcon from '@hellodarwin/icons/dist/icons/File';
import GoogleDriveFolder from '@hellodarwin/icons/dist/icons/GoogleDriveFolder';
import UploadIcon from '@hellodarwin/icons/dist/icons/Upload';
import { Alert, Button, Tooltip } from 'antd';
import { useEffect, useState } from 'react';
import { RelatedAssetActions, RelatedAssetActionButton, RelatedAssetCard, RelatedAssetIcon, RelatedAssetList, RelatedAssetMeta, RelatedAssetName, RelatedAssetText, RelatedAssetSection, RelatedFilesCount, RelatedFilesHeader, RelatedFilesNotice, RelatedFolderChevron, RelatedFolderIcon, RelatedFolderName, RelatedFolderSummary, RelatedFolderGroup, RelatedFilesLoading, RelatedFilesPanel, RelatedFilesSections, } from './styles';
const emptyRelatedAssets = () => ({
    company_folders: [],
    application_folders: [],
});
const AssetCard = ({ asset, handleCopyFile, flat = false, source, }) => {
    var _a, _b;
    const { t } = useTranslations();
    return (_jsxs(RelatedAssetCard, { "$flat": flat, children: [_jsx(RelatedAssetIcon, { "aria-hidden": true, children: _jsx(FileIcon, {}) }), _jsxs(RelatedAssetText, { children: [_jsx(RelatedAssetName, { children: _jsx(Typography, { elementTheme: "body2", children: (_b = (_a = asset.display_name) !== null && _a !== void 0 ? _a : asset.file_name) !== null && _b !== void 0 ? _b : asset.asset_id }) }), source ? (_jsx(RelatedAssetMeta, { children: _jsx(Typography, { elementTheme: "body3", children: source }) })) : null] }), _jsx(RelatedAssetActions, { children: flat ? (_jsx(HdButton, { variant: "outline", size: "small", fitContent: true, onClick: () => void handleCopyFile(asset), children: t('common|applicationDocumentDrawer.attach') })) : (_jsx(Tooltip, { title: t('assetsUpload|copy_file'), children: _jsx(RelatedAssetActionButton, { type: "button", "aria-label": t('assetsUpload|copy_file'), onClick: () => handleCopyFile(asset), children: _jsx(UploadIcon, { "aria-hidden": true }) }) })) })] }));
};
const AssetSection = ({ folder, handleCopyFile, defaultOpen, }) => {
    return (_jsxs(RelatedAssetSection, { open: defaultOpen, children: [_jsxs(RelatedFolderSummary, { children: [_jsx(RelatedFolderChevron, { "aria-hidden": true, children: _jsx(Chevron, { direction: "right" }) }), _jsx(RelatedFolderIcon, { "aria-hidden": true, children: _jsx(GoogleDriveFolder, {}) }), _jsx(RelatedFolderName, { title: folder.folder_name, children: _jsx(Typography, { elementTheme: "body3", children: folder.folder_name }) })] }), _jsx(RelatedAssetList, { children: folder.assets.map((asset) => (_jsx(AssetCard, { asset: asset, handleCopyFile: handleCopyFile }, asset.asset_id))) })] }));
};
const GinStepUploadFormRelatedFiles = ({ assets, handleCopyFile, fetchRelatedAssets, title, flat = false, }) => {
    const { t } = useTranslations();
    const panelTitle = title !== null && title !== void 0 ? title : t('common|relatedFiles.title');
    const [ginRelatedAssets, setGinRelatedAssets] = useState(emptyRelatedAssets);
    const [isLoading, setIsLoading] = useState(true);
    const [hasLoadError, setHasLoadError] = useState(false);
    const [loadAttempt, setLoadAttempt] = useState(0);
    useEffect(() => {
        let active = true;
        setIsLoading(true);
        setHasLoadError(false);
        void fetchRelatedAssets()
            .then((relatedAssets) => {
            if (active)
                setGinRelatedAssets(relatedAssets);
        })
            .catch(() => {
            if (active) {
                setGinRelatedAssets(emptyRelatedAssets());
                setHasLoadError(true);
            }
        })
            .finally(() => {
            if (active)
                setIsLoading(false);
        });
        return () => {
            active = false;
        };
    }, [fetchRelatedAssets, loadAttempt]);
    const retryLoad = () => {
        setIsLoading(true);
        setHasLoadError(false);
        setLoadAttempt((attempt) => attempt + 1);
    };
    const assetIds = new Set(assets === null || assets === void 0 ? void 0 : assets.map((asset) => asset.asset_id));
    const filterFolders = (folders) => folders.flatMap((folder) => {
        const seen = new Set();
        const filteredAssets = folder.assets.filter((asset) => {
            const isDuplicate = seen.has(asset.asset_id);
            const shouldExclude = assetIds.has(asset.asset_id);
            seen.add(asset.asset_id);
            return !shouldExclude && !isDuplicate;
        });
        return filteredAssets.length > 0
            ? [Object.assign(Object.assign({}, folder), { assets: filteredAssets })]
            : [];
    });
    const folderGroups = [
        {
            key: 'company',
            title: t('common|relatedFiles.companyFolders'),
            folders: filterFolders(ginRelatedAssets.company_folders),
        },
        {
            key: 'applications',
            title: t('common|relatedFiles.applicationFolders'),
            folders: filterFolders(ginRelatedAssets.application_folders),
        },
    ].filter(({ folders }) => folders.length > 0);
    const seenFlatAssetIds = new Set();
    const flatAssets = [
        ...ginRelatedAssets.company_folders.map((folder) => (Object.assign(Object.assign({}, folder), { source: t('common|applicationDocumentDrawer.companySource') }))),
        ...ginRelatedAssets.application_folders.map((folder) => (Object.assign(Object.assign({}, folder), { source: t('common|applicationDocumentDrawer.applicationSource', {
                application: folder.folder_name,
            }) }))),
    ]
        .flatMap((folder) => folder.assets.map((asset) => ({ asset, source: folder.source })))
        .filter(({ asset }) => {
        const isDuplicate = seenFlatAssetIds.has(asset.asset_id);
        const shouldExclude = assetIds.has(asset.asset_id);
        seenFlatAssetIds.add(asset.asset_id);
        return !shouldExclude && !isDuplicate;
    });
    if (isLoading) {
        return (_jsxs(RelatedFilesPanel, { "aria-busy": "true", children: [_jsx(Typography.Title, { level: 2, elementTheme: "h5", bold: true, noMargin: true, children: panelTitle }), _jsxs(RelatedFilesLoading, { role: "status", "aria-live": "polite", children: [_jsx(Spin, { size: "small" }), _jsx(Typography, { elementTheme: "body3", children: t('common|relatedFiles.loading') })] })] }));
    }
    if (hasLoadError) {
        return (_jsxs(RelatedFilesPanel, { children: [_jsx(Typography.Title, { level: 2, elementTheme: "h5", bold: true, noMargin: true, children: panelTitle }), _jsx(Alert, { type: "error", showIcon: true, message: t('common|relatedFiles.error'), action: _jsx(Button, { size: "small", onClick: retryLoad, children: t('common|relatedFiles.retry') }) })] }));
    }
    if (flat ? flatAssets.length === 0 : folderGroups.length === 0)
        return null;
    return (_jsxs(RelatedFilesPanel, { "$flat": flat, children: [_jsxs(RelatedFilesHeader, { children: [_jsx(Typography.Title, { level: 2, elementTheme: "h5", bold: true, noMargin: true, children: panelTitle }), flat ? (_jsx(RelatedFilesCount, { children: _jsx(Typography, { elementTheme: "body3", children: t('common|applicationDocumentDrawer.suggestionCount', {
                                count: flatAssets.length,
                            }) }) })) : null] }), flat ? (_jsx(RelatedFilesNotice, { children: _jsx(Typography, { elementTheme: "body3", children: t('common|applicationDocumentDrawer.suggestionsNotice') }) })) : null, flat ? (_jsx(RelatedAssetList, { "$flat": true, children: flatAssets.map(({ asset, source }) => (_jsx(AssetCard, { asset: asset, handleCopyFile: handleCopyFile, flat: true, source: source }, asset.asset_id))) })) : (_jsx(RelatedFilesSections, { children: folderGroups.map((group) => (_jsxs(RelatedFolderGroup, { children: [_jsx(Typography, { elementTheme: "body2", medium: true, children: group.title }), group.folders.map((folder, index) => (_jsx(AssetSection, { folder: folder, handleCopyFile: handleCopyFile, defaultOpen: group.key === 'company' && index === 0 }, folder.folder_id)))] }, group.key))) }))] }));
};
export default GinStepUploadFormRelatedFiles;
